import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import TambaLogo from "@/components/TambaLogo";
import { 
  ArrowLeft, 
  Check, 
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.info("Please sign in first");
      navigate("/auth");
      return;
    }

    setLoading(planId);
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
        user_id: user.id
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const plans = [
    {
      id: "monthly",
      name: "Monthly",
      price: "$9.99",
      period: "/month",
      description: "Perfect for getting started",
      features: [
        "AI Financial Advisor (Unlimited)",
        "Budget & Expense Tracking",
        "Goal Setting & Progress",
        "Spending Insights",
        "Email Support"
      ],
      popular: false
    },
    {
      id: "annual",
      name: "Annual",
      price: "$79.99",
      period: "/year",
      subPrice: "~$6.67/month",
      description: "Best value - save 33%",
      features: [
        "Everything in Monthly",
        "2 Months FREE",
        "Annual Financial Review",
        "Export Reports (PDF/CSV)",
        "Priority Support",
        "Early Access to Features"
      ],
      popular: true,
      savings: "Save 33%"
    }
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Decorative backgrounds */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-tamba-blue/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-tamba-yellow/10 rounded-full blur-3xl" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(user ? "/dashboard" : "/")}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <TambaLogo className="h-10 w-auto" />
            </div>
          </div>
          
          {!user && (
            <Button 
              variant="outline"
              onClick={() => navigate("/auth")}
              className="rounded-full border-tamba-blue text-tamba-blue hover:bg-tamba-blue/5"
              data-testid="login-btn"
            >
              Sign In
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Choose Your Plan
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Invest in your financial future. Cancel anytime.
          </p>
        </motion.div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`h-full relative ${
                plan.popular 
                  ? "border-tamba-yellow/50 bg-gradient-to-b from-tamba-yellow/5 to-transparent shadow-xl" 
                  : "border-border/50 hover:border-tamba-blue/30"
              } transition-all hover:shadow-lg`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-tamba-yellow text-black px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <CardContent className="p-8">
                  <div className="mb-6">
                    <h2 className="font-heading text-2xl font-semibold mb-1">{plan.name}</h2>
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  </div>
                  
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className={`font-heading text-4xl font-bold ${plan.popular ? 'text-tamba-yellow' : 'text-tamba-blue'}`}>
                        {plan.price}
                      </span>
                      <span className="text-muted-foreground">{plan.period}</span>
                    </div>
                    {plan.subPrice && (
                      <p className="text-sm text-muted-foreground mt-1">{plan.subPrice}</p>
                    )}
                    {plan.savings && (
                      <span className="inline-block mt-2 bg-tamba-teal/20 text-tamba-teal px-3 py-1 rounded-full text-xs font-medium">
                        {plan.savings}
                      </span>
                    )}
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm">
                        <Check className="h-5 w-5 text-tamba-teal flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading === plan.id}
                    className={`w-full rounded-full py-6 text-lg ${
                      plan.popular 
                        ? "bg-tamba-yellow hover:bg-tamba-yellow/90 text-black" 
                        : "bg-tamba-blue hover:bg-tamba-blue/90 text-white"
                    }`}
                    data-testid={`subscribe-${plan.id}-btn`}
                  >
                    {loading === plan.id ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      "Subscribe Now"
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* FAQ or Trust Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-16 text-center"
        >
          <p className="text-muted-foreground mb-4">
            🔒 Secure payment powered by Stripe
          </p>
          <p className="text-sm text-muted-foreground">
            Questions? Contact us at support@tamba.app
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default PricingPage;
